import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { MapPin, CheckCircle2, AlertTriangle, Loader2, Navigation } from 'lucide-react';

/**
 * Page publique de partage de position — /pos/:token
 *
 * Volontairement mono-tâche : une seule action possible, partager sa position.
 *
 * Le lien porte le jeton secret de la commande, jamais son numéro. Avec le
 * numéro, qui se devine, n'importe qui pouvait ouvrir /pos/6881, /pos/6882…
 * et déplacer la position d'un client — le livreur partait au mauvais endroit.
 * Lecture et écriture passent par deux fonctions serveur qui ne reconnaissent
 * que le jeton : la page ne touche jamais directement à la table.
 */

const NAVY = '#173b78';
const RED  = '#c53a2d';

// Statuts pour lesquels partager sa position a encore un sens
const OPEN_STATUSES = ['pending', 'transit'];

/** Ce que la fonction serveur expose : prénom, boutique, statut, et si une position existe déjà. */
async function loadByToken(token) {
  const { data } = await supabase.rpc('client_order_view', { p_token: token });
  const row = data?.[0];
  if (!row) return null;
  return { client: row.client_first_name, store: row.store, status: row.status, has_position: row.has_position };
}

export default function SharePosition() {
  const { id } = useParams();

  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [sharing, setSharing]   = useState(false);
  const [shared, setShared]     = useState(false);
  const [accuracy, setAccuracy] = useState(null);
  const [error, setError]       = useState('');

  const watchIdRef = useRef(null);

  // ── Chargement de la commande ────────────────────────────────────
  useEffect(() => {
    if (!id) { setLoading(false); setNotFound(true); return; }
    let cancelled = false;

    (async () => {
      const found = await loadByToken(String(id).trim());
      if (cancelled) return;
      setLoading(false);
      if (!found) { setNotFound(true); return; }
      setDelivery(found);
      if (found.has_position) setShared(true);
    })();

    return () => { cancelled = true; };
  }, [id]);

  // ── Arrêt du suivi ───────────────────────────────────────────────
  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => stopWatching, [stopWatching]);

  // ── Envoi d'une position ─────────────────────────────────────────
  const pushPosition = useCallback(async (position) => {
    const { latitude, longitude, accuracy: acc } = position.coords;

    const { data: accepted, error: rpcError } = await supabase.rpc('client_order_share_position', {
      p_token: id,
      p_lat: latitude,
      p_lng: longitude,
      p_accuracy: acc ?? null,
    });

    if (rpcError) {
      setError("La position n'a pas pu être envoyée. Vérifiez votre connexion.");
      return;
    }
    // Refusée : la livraison vient de se terminer. On cesse de suivre et on
    // relit son statut pour afficher l'écran de fin.
    if (!accepted) {
      stopWatching();
      const fresh = await loadByToken(id);
      if (fresh) setDelivery(fresh);
      return;
    }
    setError('');
    setShared(true);
    setAccuracy(acc ?? null);
  }, [id, stopWatching]);

  const handleShare = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Votre téléphone ne permet pas le partage de position.");
      return;
    }
    setError('');
    setSharing(true);

    // Premier point : on confirme au client le plus vite possible.
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await pushPosition(position);
        setSharing(false);

        // Puis on suit ses déplacements tant que la livraison est en cours,
        // pour que le livreur ne parte pas sur une position périmée.
        if (watchIdRef.current === null) {
          watchIdRef.current = navigator.geolocation.watchPosition(
            pushPosition,
            () => {},
            { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
          );
        }
      },
      (geoError) => {
        setSharing(false);
        setError(
          geoError.code === geoError.PERMISSION_DENIED
            ? "Vous avez refusé le partage. Autorisez la position dans les réglages de votre navigateur, puis réessayez."
            : "Impossible d'obtenir votre position. Placez-vous près d'une fenêtre et réessayez."
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [pushPosition]);

  // ── Rendu ────────────────────────────────────────────────────────
  const page = (children) => (
    <div style={{
      minHeight: '100dvh', background: '#f0f3f8',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '24px 20px', boxSizing: 'border-box',
      fontFamily: 'Alan Sans, system-ui, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>{children}</div>
    </div>
  );

  if (loading) {
    return page(
      <div style={{ textAlign: 'center', color: '#94a3b8' }}>
        <Loader2 size={26} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: '12px', fontSize: '14px' }}>Chargement…</div>
      </div>
    );
  }

  if (notFound) {
    return page(
      <div style={{ background: 'white', borderRadius: '20px', padding: '36px 24px', textAlign: 'center', border: '1px solid #eef0f6' }}>
        <AlertTriangle size={30} color="#c2410c" style={{ marginBottom: '14px' }} />
        <div style={{ fontSize: '17px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Lien introuvable</div>
        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>
          Ce lien n'est plus valide. Contactez votre livreur pour en recevoir un nouveau.
        </div>
      </div>
    );
  }

  const closed = !OPEN_STATUSES.includes(delivery.status);

  // Livraison terminée : le lien ne sert plus, on ne propose plus rien.
  if (closed) {
    return page(
      <div style={{ background: 'white', borderRadius: '20px', padding: '36px 24px', textAlign: 'center', border: '1px solid #eef0f6' }}>
        <CheckCircle2 size={32} color="#15803d" style={{ marginBottom: '14px' }} />
        <div style={{ fontSize: '17px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
          {delivery.status === 'delivered' ? 'Commande déjà livrée' : 'Livraison clôturée'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>
          Le partage de position n'est plus nécessaire. Merci de votre confiance.
        </div>
      </div>
    );
  }

  const firstName = String(delivery.client || '').trim().split(' ')[0];

  return page(
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* En-tête */}
      <div style={{ textAlign: 'center', marginBottom: '22px' }}>
        <img
          src="/logo-wakaana.png"
          alt="Wakaana Express"
          style={{ height: '56px', objectFit: 'contain', margin: '0 auto 6px' }}
        />
      </div>

      <div style={{
        background: 'white', borderRadius: '22px', padding: '28px 22px 26px',
        border: '1px solid #eef0f6', boxShadow: '0 8px 30px rgba(19,40,72,0.08)', textAlign: 'center',
      }}>
        {shared ? (
          <>
            <div style={{
              width: '68px', height: '68px', borderRadius: '20px', background: '#f0fdf4',
              border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <CheckCircle2 size={32} color="#15803d" />
            </div>
            <div style={{ fontSize: '19px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
              Position envoyée
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.65 }}>
              Votre livreur peut maintenant vous localiser.
              {watchIdRef.current !== null && <><br />Elle se met à jour si vous vous déplacez.</>}
            </div>
            {accuracy != null && (
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '12px' }}>
                Précision : environ {Math.round(accuracy)} m
              </div>
            )}
            <button
              onClick={handleShare}
              disabled={sharing}
              style={{
                marginTop: '20px', width: '100%', padding: '13px', borderRadius: '13px',
                border: '1.5px solid #e2e8f0', background: 'white', color: '#475569',
                fontSize: '14px', fontWeight: 700, cursor: sharing ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {sharing ? 'Actualisation…' : 'Actualiser ma position'}
            </button>
          </>
        ) : (
          <>
            <div style={{
              width: '72px', height: '72px', borderRadius: '22px',
              background: 'linear-gradient(135deg, #f0f4ff, #dbeafe)', border: '1px solid #c7d7ef',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
            }}>
              <MapPin size={32} color={NAVY} />
            </div>

            <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '10px', lineHeight: 1.3 }}>
              {firstName ? `Bonjour ${firstName},` : 'Bonjour,'}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.65, marginBottom: '26px' }}>
              Partagez votre position pour que votre livreur vous apporte
              {delivery.store ? <> votre commande <strong style={{ color: '#0f172a' }}>{delivery.store}</strong></> : ' votre commande'} sans avoir à vous appeler.
            </div>

            <button
              onClick={handleShare}
              disabled={sharing}
              style={{
                width: '100%', padding: '18px', borderRadius: '15px', border: 'none',
                background: sharing ? '#94a3b8' : `linear-gradient(135deg, ${RED}, #8b1e16)`,
                color: 'white', fontSize: '16px', fontWeight: 800, cursor: sharing ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px',
                boxShadow: sharing ? 'none' : '0 8px 22px rgba(197,58,45,0.35)',
                fontFamily: 'inherit',
              }}
            >
              {sharing
                ? <><Loader2 size={19} style={{ animation: 'spin 1s linear infinite' }} /> Localisation…</>
                : <><Navigation size={19} /> Partager ma position</>}
            </button>

            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '14px', lineHeight: 1.6 }}>
              Votre téléphone vous demandera d'autoriser l'accès.
              <br />Position utilisée uniquement pour cette livraison.
            </div>
          </>
        )}

        {error && (
          <div style={{
            marginTop: '18px', background: '#fff1f2', border: '1px solid #fecdd3',
            borderRadius: '12px', padding: '12px 14px', fontSize: '13px', color: '#be123c',
            lineHeight: 1.55, textAlign: 'left',
          }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '20px' }}>
        Wakaana Express · Votre partenaire e-commerce
      </div>
    </>
  );
}
